import { faHandshake } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import qs from 'qs';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import LOBanner from '../assets/img/lo-banner.svg';
import Logo from '../assets/img/real_logo_outline.svg';
import AnalyticsEventOnLoad from '../components/Analytics/AnalyticsEventOnLoad';
import DefaultSplash from '../components/DefaultSplash.tsx';
import FieldErrorMessage from '../components/inputs/FieldErrorMessage.tsx';
import AffiliatedBusinessArrangementModal from '../components/Modals/AffiliatedBusinessArrangementModal.tsx';
import PartnerProfileCard from '../components/PartnerProfileCard.tsx';
import {
  LoanOfficerApi,
  LoanOfficerDetailDto,
  PartnerDto,
} from '../openapi/atlantis';
import AnalyticsService from '../services/AnalyticsService';
import ErrorService from '../services/ErrorService.ts';
import { AnalyticsEventEnum } from '../types';
import Logger from '../utils/Logger.ts';
import { getAgentPartnerEventProperties } from '../utils/OnboardingAnalyticsUtils';
import { getAtlantisConfiguration } from '../utils/OpenapiConfigurationUtils.tsx';
import { EMAIL_VALIDATIONS } from '../utils/Validations.tsx';
import Route404 from './Route404.tsx';

interface FormData {
  emailAddress: string;
}

const AgentPartnerRoute: React.FC = () => {
  const navigate = useNavigate();
  const { loanOfficeSlug, partnerSlug } = useParams<{
    loanOfficeSlug: string;
    partnerSlug: string;
  }>();

  const [loading, setLoading] = useState<boolean>(false);
  const [partner, setPartner] = useState<PartnerDto>();
  const [loanOfficer, setLoanOfficer] = useState<LoanOfficerDetailDto>();
  const [viewABusinessAgreement, setViewABusinessAgreement] =
    useState<boolean>(false);
  const [eventProperties, setEventProperties] = useState<Record<
    string,
    unknown
  > | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const fetchAgentBySlug = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await new LoanOfficerApi(
        await getAtlantisConfiguration(),
      ).getLoanOfficerAndPartnerBySlug(loanOfficeSlug!, partnerSlug!);
      setPartner(data?.partner);
      setLoanOfficer(data?.loanOfficer);
    } catch (e: any) {
      Logger.error('Unable to fetch authorized person:', e);
      ErrorService.notify('Unable to fetch authorized person', e);
    } finally {
      setLoading(false);
    }
  }, [loanOfficeSlug, partnerSlug]);

  useEffect(() => {
    fetchAgentBySlug();
  }, [fetchAgentBySlug]);

  const prepareEventProperties = useCallback(async () => {
    if (loanOfficer && partner) {
      const properties = await getAgentPartnerEventProperties(
        loanOfficer,
        partner,
      );
      setEventProperties(properties);
    }
  }, [loanOfficer, partner]);

  // Prepare event properties when loan officer and partner data is loaded
  useEffect(() => {
    prepareEventProperties();
  }, [prepareEventProperties]);

  const onSubmit = (values: FormData) => {
    // Track Get Started button click with prepared event properties
    if (eventProperties) {
      AnalyticsService.instance().logEvent(
        AnalyticsEventEnum.AGENT_PARTNER_GET_STARTED_CLICKED,
        eventProperties,
      );
    }

    const signupUrl = `/signup?${qs.stringify({
      emailAddress: values.emailAddress,
      inviterAgentId: partner?.userId,
      loanOfficerId: loanOfficer?.id,
    })}`;

    navigate(signupUrl);
  };

  if (loading) {
    return (
      <div className='h-screen w-screen'>
        <DefaultSplash />
      </div>
    );
  }

  if (!partner || !loanOfficer) {
    return <Route404 />;
  }

  return (
    <div className='bg-reskin-green-900 h-screen w-screen overflow-scroll'>
      <div className='flex flex-row items-center justify-between ml-6 lg:mx-40 lg:my-10 m-4'>
        <img src={Logo} alt='Logo' className='cursor-pointer' />
      </div>
      <div className='grid lg:grid-cols-2 grid-cols-1 gap-10 lg:pl-40 lg:my-10'>
        <div className='m-5 lg:mx-0 lg:mt-0 lg:max-h-[354px] flex flex-col-reverse lg:flex-col gap-7 lg:gap-0'>
          <div className='flex flex-col lg:flex-row items-start gap-3 relative'>
            <PartnerProfileCard authorizedPerson={loanOfficer} isLoanOfficer />
            <div className='absolute top-[47%] lg:top-[44%] left-[45%] lg:left-[46.5%] bg-[#1A454A] rounded-full w-10 h-10 flex justify-center items-center border-8 p-5 border-reskin-green-900 z-10'>
              <FontAwesomeIcon
                icon={faHandshake}
                className='text-reskin-primary-light text-xl '
              />
            </div>
            <PartnerProfileCard authorizedPerson={partner} isPartner />
          </div>
          <div>
            <div className='md:mt-18 space-y-2'>
              <p className='font-inter-light text-[26px] md:text-[42px] text-reskin-aqua md:mt-10 leading-8 md:leading-[50px] md:w-[90%]'>
                Your Homebuying Journey Starts Here!
              </p>
              <p className='font-inter-light font-normal text-white text-base md:text-lg'>
                Enter your email to apply for your mortgage today.
              </p>
            </div>
            <div className='mt-8 '>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className='flex flex-row md:w-[405px] w-full'
              >
                <input
                  placeholder='Your email address'
                  className='py-2 md:py-4 px-3 font-inter-regular text-base md:w-1/3 rounded-l-[4px] flex-1'
                  {...register('emailAddress', {
                    required: 'Please enter email address',
                    ...EMAIL_VALIDATIONS,
                  })}
                />
                <button
                  type='submit'
                  className='px-4 md:px-8 py-2 md:py-4 bg-reskin-midnight-green-700 rounded-r'
                >
                  <span className='font-inter-regular text-white md:text-base text-sm'>
                    Get Started!
                  </span>
                </button>
              </form>
              {!!errors.emailAddress?.message && (
                <div className='mt-2'>
                  <FieldErrorMessage message={errors.emailAddress.message} />
                </div>
              )}
            </div>
            <div className='mt-4'>
              <p className='font-inter-regular text-sm md:text-base text-white'>
                View the{' '}
                <a
                  href='#'
                  className='text-reskin-aqua font-inter-medium text-base cursor-pointer ml-1'
                  onClick={() => {
                    setViewABusinessAgreement(true);
                  }}
                >
                  Affiliated Business Agreement
                </a>
              </p>
            </div>
            <div className='mt-4'>
              <p className='font-inter-regular text-sm md:text-base text-white'>
                Already have an account?{' '}
                <a
                  href={`/login?inviterAgentId=${partner?.userId}&loanOfficerId=${loanOfficer?.id}`}
                  className='text-reskin-aqua font-inter-medium text-base cursor-pointer ml-1'
                >
                  Sign In
                </a>
              </p>
            </div>
          </div>
        </div>
        <div className='flex flex-row justify-end ml-5'>
          <img src={LOBanner} alt='logo' className='object-contain' />
        </div>
      </div>

      {viewABusinessAgreement && (
        <AffiliatedBusinessArrangementModal
          onClose={() => setViewABusinessAgreement(false)}
          showSignature={false}
        />
      )}

      {eventProperties && (
        <AnalyticsEventOnLoad
          eventName={AnalyticsEventEnum.AGENT_PARTNER_PAGE_VIEWED}
          eventData={eventProperties}
        />
      )}
    </div>
  );
};

export default AgentPartnerRoute;
